import type {
  BulkManageRow,
  BulkManageFactorRow,
  BulkManagePortfolioRow,
  BulkManageFundRow,
  CommonRangeData,
} from '../../types';
import { useTheme } from 'styled-components';
import {
  isBulkManageFactorRow,
  isBulkManageFundRow,
  isBulkManagePortfolioRow,
  isHistoricalBulkManageInvestmentRow,
} from '../../utils';
import { assertNotNil } from 'venn-utils';
import { FillParentTooltip } from '../tooltip-cells';
import { atStartOfDay, getDataColor, getHistoricalRowRangeLogic, getRowRangeLogic } from '../../analysisPeriodUtils';
import { compact } from 'lodash';
import ProxiedInvestmentTooltipContent from '../../ProxiedInvestmentTooltipContent';
import React from 'react';
import { RangeCellRendererPrimitives } from './RangeCellRendererPrimitives';
import { Match } from 'effect';

const ANALYSIS_BAR_HEIGHT = 8;
const HISTORICAL_BAR_HEIGHT = ANALYSIS_BAR_HEIGHT / 2;
const LIQUIDATION_BAR_HEIGHT = ANALYSIS_BAR_HEIGHT;

type RangeCellProps = {
  data: (BulkManageFactorRow | BulkManagePortfolioRow | BulkManageFundRow) & {
    commonRangeData: Exclude<CommonRangeData, undefined>;
  };
  shouldShowHypotheticalReturnExtension: (row: BulkManageRow) => boolean;
};

export const InvestmentRangeCell = ({ data, shouldShowHypotheticalReturnExtension }: RangeCellProps) => {
  const theme = useTheme();
  const { earliestStart, latestEnd, fullRange, overlap } = data.commonRangeData;

  const zeroAllocation =
    !isBulkManageFactorRow(data) && !isHistoricalBulkManageInvestmentRow(data) && data.allocation === 0;
  const { investmentColor, proxyColor, extrapolationColor, historicalColor, hypotheticalReturnExtensionColor } =
    getDataColor(theme, zeroAllocation);

  const frequency = data.frequency;
  const startDate = assertNotNil(atStartOfDay(data.startDate));
  const endDate = assertNotNil(data.endDate);
  const proxyEndDate = isBulkManageFundRow(data) ? data.proxyEndDate : undefined;
  const extrapolateEndDate = isBulkManageFundRow(data) ? data.extrapolateEndDate : undefined;
  const finalReturnDate = Math.max(...compact([endDate, proxyEndDate, extrapolateEndDate]));

  const { proxy, investment, extrapolation } = getRowRangeLogic({
    startDate,
    endDate,
    fullRange,
    earliestStart,
    latestEnd,
    proxyStartDate: atStartOfDay(isBulkManageFundRow(data) ? data.proxyStartDate : undefined),
    proxyEndDate,
    extrapolateEndDate,
    extrapolateStartDate: atStartOfDay(isBulkManageFundRow(data) ? data.extrapolateStartDate : undefined),
  });
  const historical = Match.value(data).pipe(
    Match.when(isBulkManagePortfolioRow, () => undefined),
    Match.when(isHistoricalBulkManageInvestmentRow, ({ allocation }) =>
      getHistoricalRowRangeLogic({
        fullRange,
        earliestStart,
        latestEnd,
        historicalAllocation: allocation,
        finalReturnDate,
      }),
    ),
    Match.orElse(() => undefined),
  );
  const barFillType = Match.value({ zeroAllocation, frequency }).pipe(
    Match.when({ zeroAllocation: true }, () => 'diagonal striped' as const),
    Match.whenOr({ frequency: 'YEARLY' }, { frequency: 'QUARTERLY' }, () => 'striped' as const),
    Match.orElse(() => 'solid' as const),
  );
  return (
    <div className="flex flex-col relative h-full w-full justify-center gap-[2px]">
      <RangeCellRendererPrimitives.OverlapBar
        percentageWidth={overlap.percentageWidth}
        percentageStart={overlap.percentageStart}
      />
      <div className="relative" style={{ height: `${ANALYSIS_BAR_HEIGHT}px` }}>
        <RangeCellRendererPrimitives.RangeBar
          height={ANALYSIS_BAR_HEIGHT}
          widthPercentage={investment.percentageWidth}
          leftMarginPercentage={investment.percentageStart}
          fillType={barFillType}
          color={investmentColor}
        >
          {proxy.percentageWidth > 0 && (
            <RangeCellRendererPrimitives.RangeBar
              height={ANALYSIS_BAR_HEIGHT}
              widthPercentage={proxy.percentageWidth}
              leftMarginPercentage={proxy.percentageStart}
              color={proxyColor}
              fillType={barFillType}
            />
          )}
          {extrapolation.percentageWidth > 0 && (
            <RangeCellRendererPrimitives.RangeBar
              height={ANALYSIS_BAR_HEIGHT}
              widthPercentage={extrapolation.percentageWidth}
              leftMarginPercentage={extrapolation.percentageStart}
              color={extrapolationColor}
              fillType={barFillType}
            />
          )}
        </RangeCellRendererPrimitives.RangeBar>
        {shouldShowHypotheticalReturnExtension(data) && historical && historical.hypotheticalReturnExtension && (
          <RangeCellRendererPrimitives.RangeBar
            height={ANALYSIS_BAR_HEIGHT}
            widthPercentage={historical.hypotheticalReturnExtension.percentageWidth}
            leftMarginPercentage={historical.hypotheticalReturnExtension.percentageStart}
            color={hypotheticalReturnExtensionColor}
            fillType="diagonal striped"
          />
        )}
      </div>
      {historical && (
        <div className="relative" style={{ height: `${LIQUIDATION_BAR_HEIGHT}px` }}>
          {historical.rowRanges.map((range, index) => (
            <RangeCellRendererPrimitives.RangeBarWithGap
              key={`${data.fullPath}-historical-${range.percentageStart}`}
              height={HISTORICAL_BAR_HEIGHT}
              hasGap={index > 0}
              widthPercentage={range.percentageWidth}
              leftMarginPercentage={range.percentageStart}
              color={historicalColor}
              fillType={barFillType}
            />
          ))}
          {historical.driftedRange && (
            <RangeCellRendererPrimitives.RangeBar
              height={HISTORICAL_BAR_HEIGHT}
              widthPercentage={historical.driftedRange.percentageWidth}
              leftMarginPercentage={historical.driftedRange.percentageStart}
              color={historicalColor}
              fillType="spaced circles"
            />
          )}
          {historical.isFinalAllocationLiquidation && (
            <RangeCellRendererPrimitives.RangeBar
              height={LIQUIDATION_BAR_HEIGHT}
              widthPercentage={0}
              leftMarginPercentage={
                (historical.rowRanges[historical.rowRanges.length - 1]?.percentageStart ?? 0) +
                (historical.rowRanges[historical.rowRanges.length - 1]?.percentageWidth ?? 0)
              }
              color={historicalColor}
              fillType={barFillType}
            />
          )}
        </div>
      )}
      <FillParentTooltip variant="overlap" maxWidth={400} content={<ProxiedInvestmentTooltipContent data={data} />} />
    </div>
  );
};
